import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { ChartType } from 'angular-google-charts';
import {
    Challenge,
    Committee,
    CommitteesGQL,
    Country,
    GetChallengesByLanguageAllGQL,
    GetCountriesGQL,
    GetUnitiesGQL,
    Unity,
} from '../../../generated/graphql';
import { BaseComponent } from '../../base/base.component';
import {
    AwardedIdeaListPaginator,
    DashboardApiService,
    GeneralPerformanceByUnityResult,
    GeneralPerformanceDashboardResult,
    GeneralPerformanceGainsResult,
    IdeasByMonthResult,
    IdeaCommitteeDashboardResult,
    IdeaDashboardPage,
    SearchGeneralPerformanceDashboardInput,
    SearchIdeaCommitteeDashboardInput,
} from './dashboard-api.service';

/** Card de status da aba Comitês; `mock` = dado ilustrativo (ainda sem fonte real no backend). */
interface StatusCard {
    key: string;
    label?: string;
    labelKey?: string;
    total: number | null;
    mock: boolean;
}

/** Linha mock dos blocos Ganhos/Investimento (aba Performance Geral). */
/** Linha dos blocos Ganhos/Investimento: `field` = total REAL do backend; sem `field` = ILUSTRATIVO ("—" + selo). */
interface GainsItem {
    icon: string;
    labelKey: string;
    field?: keyof GeneralPerformanceGainsResult;
    format?: 'currency' | 'number';
    suffixKey?: string;
}

interface GainsColumn {
    titleKey: string;
    icon: string;
    items: GainsItem[];
}

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent extends BaseComponent implements OnInit {

    panel = 1;

    // ---- Aba "Comitês" (panel === 1) — dados reais ----------------------------
    committeeDashboard: IdeaCommitteeDashboardResult | null = null;
    /** Cards "Quantidade de ideias por status" (ordem definida pela cliente; ver buildStatusCards). */
    statusCards: StatusCard[] = [];
    listPage: IdeaDashboardPage | null = null;
    page = 1;

    // filtros da aba Comitês (unidade NÃO se aplica a estas queries)
    filterCountryId?: string;
    filterCommitteeId?: string;
    filterChallengeId?: string;
    filterStatus?: string;
    filterYear?: number;

    // lookups dos selects
    listCountry: Country[] = [];
    committeeResult: Committee[] = [];
    challengeResult: Challenge[] = [];

    // status fixos aceitos pela query de comitês
    dashboardStatuses: string[] = [
        'received', 'analysis', 'approved_award', 'awarded',
        'denied', 'denial', 'converted', 'finished',
    ];

    // opções de ano
    yearsList: number[] = [];

    // gráfico "Total de Ideias Registradas": 1 série = monthlyChart[].total
    // (backend: COUNT(*) de ideias agrupado por MONTH(createdAt), com os filtros aplicados)
    chart = {
        type: ChartType.ColumnChart,
        columns: ['Mês', 'Registradas'],
        data: [] as (string | number)[][],
        options: {
            title: '',
            isStacked: false,
            height: 400,
            // sem largura fixa: o gráfico segue a largura do container via [dynamicResize]
            chartArea: { width: '90%', height: '75%' },
            legend: { position: 'none' },
            colors: ['#5c6bc0'],
        },
    };

    // ---- Aba "Performance Geral" (panel === 2) — dados reais -------------------
    // Blocos "Ganhos"/"Investimento": Total REAL; Operações/Administrativo e ROI ILUSTRATIVOS.
    generalPerformance: GeneralPerformanceDashboardResult | null = null;
    byUnity: GeneralPerformanceByUnityResult | null = null;
    brazilMonthly: IdeasByMonthResult | null = null;
    latamMonthly: IdeasByMonthResult | null = null;
    awardedList: AwardedIdeaListPaginator | null = null;
    awardedPage = 1;

    // filtros compartilhados da aba Performance Geral
    perfUnityId?: string;
    perfCommitteeId?: string;
    performanceStatus?: string;
    perfStartDate?: string;
    perfEndDate?: string;
    // País/Ano: apenas visuais nesta fase — SearchGeneralPerformanceDashboardInput não tem
    // country/year, então NÃO entram em buildPerfInput (tooltip "em breve" no template).
    perfCountryId?: string;
    perfYear?: number;

    // ---- Ganhos / Investimento ----
    // Linha "Total" = REAL (generalPerformanceGainsDashboard: premiadas, sem traduções).
    // Operações/Administrativo (e ROI) = ILUSTRATIVO: o backend ainda não tem esse split.
    gains: GeneralPerformanceGainsResult | null = null;

    gainsColumns: GainsColumn[] = [
        {
            titleKey: 'dashboard.performance.gains.financial',
            icon: 'assets/svg/attach_money.svg',
            items: [
                { icon: 'assets/svg/pill.svg', labelKey: 'dashboard.performance.gains.operations' },
                { icon: 'assets/svg/receipt.svg', labelKey: 'dashboard.performance.gains.administrative' },
                { icon: 'assets/svg/paid.svg', labelKey: 'dashboard.performance.gains.total', field: 'financialTotal', format: 'currency' },
            ],
        },
        {
            titleKey: 'dashboard.performance.gains.productivity',
            icon: 'assets/svg/schedule.svg',
            items: [
                { icon: 'assets/svg/pill.svg', labelKey: 'dashboard.performance.gains.operations' },
                { icon: 'assets/svg/receipt.svg', labelKey: 'dashboard.performance.gains.administrative' },
                { icon: 'assets/svg/paid.svg', labelKey: 'dashboard.performance.gains.total', field: 'productivityHoursTotal', format: 'number', suffixKey: 'dashboard.performance.units.hours' },
            ],
        },
        {
            titleKey: 'dashboard.performance.gains.sustainability',
            icon: 'assets/svg/eco.svg',
            items: [
                { icon: 'assets/svg/pill.svg', labelKey: 'dashboard.performance.gains.operations' },
                { icon: 'assets/svg/receipt.svg', labelKey: 'dashboard.performance.gains.administrative' },
                { icon: 'assets/svg/paid.svg', labelKey: 'dashboard.performance.gains.total', field: 'environmentalScoreTotal', format: 'number', suffixKey: 'dashboard.performance.units.points' },
            ],
        },
    ];

    // Investimento (Reconhecimento em R$): Total real; ROI sempre ilustrativo (sem fórmula definida)
    investmentList: GainsItem[] = [
        { icon: 'assets/svg/rewarded_ads.svg', labelKey: 'dashboard.performance.gains.total', field: 'investmentTotal', format: 'currency' },
        { icon: 'assets/svg/pill.svg', labelKey: 'dashboard.performance.gains.operations' },
        { icon: 'assets/svg/receipt.svg', labelKey: 'dashboard.performance.gains.administrative' },
    ];

    listUnity: Unity[] = [];

    // gráfico: desempenho por unidade (colunas agrupadas, 3 séries)
    byUnityChart = {
        type: ChartType.ColumnChart,
        columns: ['Unidade', 'Registradas', 'Premiadas', 'Colaboradores Premiados'],
        data: [] as (string | number)[][],
        options: {
            isStacked: false,
            height: 400,
            chartArea: { width: '85%', height: '70%' },
            legend: { position: 'bottom' },
            colors: ['#5c6bc0', '#66bb6a', '#ffb300'],
        },
    };

    // gráficos mensais pivotados (1 série por unidade)
    brazilChart = this.newMonthlyChart();
    latamChart = this.newMonthlyChart();

    constructor(
        public router: Router,
        public loading: NgxSpinnerService,
        public translate: TranslateService,
        private dashboardApi: DashboardApiService,
        private getCountriesGQL: GetCountriesGQL,
        private committeesGQL: CommitteesGQL,
        private getChallengesByLanguageAllGQL: GetChallengesByLanguageAllGQL,
        private getUnitiesGQL: GetUnitiesGQL,
    ) {
        super(router, loading, translate);
    }

    ngOnInit(): void {
        const currentYear = this.dateNow.getFullYear();
        for (let y = currentYear; y >= currentYear - 4; y--) {
            this.yearsList.push(y);
        }
        this.loadLookups();
        this.loadCommitteeDashboard();
        this.loadIdeaList();
        this.searchPerformance();
    }

    // ---- lookups dos filtros (reaproveitando serviços existentes) --------------
    private loadLookups(): void {
        this.getCountriesGQL.watch().valueChanges.subscribe(({ data }) => {
            this.listCountry = (data?.getCountries as Country[]) || [];
        });
        this.committeesGQL.watch().valueChanges.subscribe(({ data }) => {
            this.committeeResult = (data?.committees as Committee[]) || [];
        });
        this.getUnitiesGQL.watch().valueChanges.subscribe(({ data }) => {
            this.listUnity = (data?.getUnities as Unity[]) || [];
        });
        const culture = this.translate.currentLang === 'es' ? 'es-ES' : 'pt-BR';
        this.getChallengesByLanguageAllGQL.watch({ input: culture }).valueChanges.subscribe(({ data }) => {
            this.challengeResult = (data?.getChallengesByLanguageAll as Challenge[]) || [];
        });
    }

    private buildInput(): SearchIdeaCommitteeDashboardInput {
        const input: SearchIdeaCommitteeDashboardInput = {};
        if (this.filterCountryId) { input.countryIds = [this.filterCountryId]; }
        if (this.filterCommitteeId) { input.committeeIds = [this.filterCommitteeId]; }
        if (this.filterChallengeId) { input.challengeIds = [this.filterChallengeId]; }
        if (this.filterStatus) { input.statuses = [this.filterStatus]; }
        if (this.filterYear) { input.year = Number(this.filterYear); }
        return input;
    }

    // ---- cards / gráfico (sempre a visão geral `all`) --------------------------
    private loadCommitteeDashboard(): void {
        this.loading.show();
        this.dashboardApi.ideaCommitteeDashboard(this.buildInput()).subscribe({
            next: ({ data }) => {
                this.committeeDashboard = data.ideaCommitteeDashboard;
                this.buildStatusCards();
                this.buildChart();
                this.loading.hide();
            },
            error: (err) => {
                this.loading.hide();
                super.onError(err);
            },
        });
    }

    /**
     * Ordem alvo: Recebida, Análise, Atrasada, Reprovada, Pontuada, Premiada.
     * Usa o item real de `all.ideas` quando a key existe; senão vira card MOCK
     * ("Recebida" hoje não vem do backend; "Atrasada" não existe no backend).
     */
    private buildStatusCards(): void {
        const ideas = this.committeeDashboard?.all?.ideas || [];
        const order: { key: string; labelKey: string }[] = [
            { key: 'received', labelKey: 'dashboard.committee.status.received' },
            { key: 'analysis', labelKey: 'dashboard.committee.status.analysis' },
            { key: 'late', labelKey: 'dashboard.committee.status.late' },
            { key: 'denied', labelKey: 'dashboard.committee.status.denied' },
            { key: 'approved_award', labelKey: 'dashboard.committee.status.approved_award' },
            { key: 'awarded', labelKey: 'dashboard.committee.status.awarded' },
        ];
        this.statusCards = order.map(({ key, labelKey }) => {
            const real = ideas.find((i) => i.key === key);
            return real
                ? { key, label: real.label, total: real.total, mock: false }
                : { key, labelKey, total: null, mock: true };
        });
    }

    private buildChart(): void {
        this.chart.data = (this.committeeDashboard?.all?.monthlyChart || []).map((m) => [
            m.monthLabel,
            m.total,
        ]);
    }

    // ---- lista de ideias paginada ---------------------------------------------
    private loadIdeaList(): void {
        this.loading.show();
        this.dashboardApi.ideaCommitteeList({ ...this.buildInput(), page: this.page }).subscribe({
            next: ({ data }) => {
                this.listPage = data.ideaCommitteeListDashboard;
                this.loading.hide();
            },
            error: (err) => {
                this.loading.hide();
                super.onError(err);
            },
        });
    }

    nextPage(): void {
        if (this.listPage?.nextPage) {
            this.page++;
            this.loadIdeaList();
        }
    }

    prevPage(): void {
        if (this.listPage?.previouPage) {
            this.page--;
            this.loadIdeaList();
        }
    }

    onSearch(): void {
        this.page = 1;
        this.loadCommitteeDashboard();
        this.loadIdeaList();
    }

    // ---- Aba "Performance Geral" -----------------------------------------------
    newMonthlyChart() {
        return {
            type: ChartType.ColumnChart,
            columns: ['Mês'] as string[],
            data: [] as (string | number)[][],
            options: {
                isStacked: false,
                height: 400,
                chartArea: { width: '85%', height: '70%' },
                legend: { position: 'bottom' },
            },
        };
    }

    /** Monta o input compartilhado dos filtros desta aba (unidade/comitê/status/período). */
    private buildPerfInput(): SearchGeneralPerformanceDashboardInput {
        const input: SearchGeneralPerformanceDashboardInput = {};
        if (this.perfUnityId) { input.unityIds = [this.perfUnityId]; }
        if (this.perfCommitteeId) { input.committeeIds = [this.perfCommitteeId]; }
        if (this.performanceStatus) { input.statuses = [this.performanceStatus]; }
        if (this.perfStartDate) { input.startDate = `${this.perfStartDate}T00:00:00.000`; }
        if (this.perfEndDate) { input.endDate = `${this.perfEndDate}T23:59:59.999`; }
        return input;
    }

    /** Botão "Pesquisar": dispara as 5 queries com o mesmo input (lista volta à página 1). */
    searchPerformance(): void {
        this.awardedPage = 1;
        this.loadGeneralPerformance();
        this.loadGains();
        this.loadByUnity();
        this.loadBrazilMonthly();
        this.loadLatamMonthly();
        this.loadAwardedList();
    }

    // alias mantido para o template
    onSearchPerformance(): void {
        this.searchPerformance();
    }

    private loadGeneralPerformance(): void {
        this.loading.show();
        this.dashboardApi.generalPerformance(this.buildPerfInput()).subscribe({
            next: ({ data }) => {
                this.generalPerformance = data.generalPerformanceDashboard;
                this.loading.hide();
            },
            error: (err) => { this.loading.hide(); super.onError(err); },
        });
    }

    private loadGains(): void {
        this.dashboardApi.generalPerformanceGains(this.buildPerfInput()).subscribe({
            next: ({ data }) => { this.gains = data.generalPerformanceGainsDashboard; },
            error: (err) => super.onError(err),
        });
    }

    /** Valor real da linha (0 enquanto a query não respondeu). */
    gainsValue(item: GainsItem): number {
        return item.field ? Number(this.gains?.[item.field] ?? 0) : 0;
    }

    private loadByUnity(): void {
        this.dashboardApi.generalPerformanceByUnity(this.buildPerfInput()).subscribe({
            next: ({ data }) => {
                this.byUnity = data.generalPerformanceByUnityDashboard;
                this.byUnityChart.data = (this.byUnity?.items || []).map((i) => [
                    i.unityName, i.ideasRegistered, i.awardedIdeas, i.awardedCollaborators,
                ]);
            },
            error: (err) => super.onError(err),
        });
    }

    private loadBrazilMonthly(): void {
        this.dashboardApi.brazilIdeasByUnityMonthly(this.buildPerfInput()).subscribe({
            next: ({ data }) => {
                this.brazilMonthly = data.brazilIdeasByUnityMonthlyDashboard;
                this.pivotMonthly(this.brazilMonthly, this.brazilChart);
            },
            error: (err) => super.onError(err),
        });
    }

    private loadLatamMonthly(): void {
        this.dashboardApi.latamIdeasByUnityMonthly(this.buildPerfInput()).subscribe({
            next: ({ data }) => {
                this.latamMonthly = data.latamIdeasByUnityMonthlyDashboard;
                this.pivotMonthly(this.latamMonthly, this.latamChart);
            },
            error: (err) => super.onError(err),
        });
    }

    /** Pivota items mensais -> colunas = ['Mês', ...todas as unidades], preenchendo 0 onde faltar. */
    /**
     * Pivota o retorno aninhado dos mensais para o formato do google-chart:
     * columns = ['Mês', ...todas as unidades] (>= 2 colunas) e data = 1 linha por mês,
     * preenchendo 0 onde a unidade não tiver total naquele mês.
     */
    private pivotMonthly(result: IdeasByMonthResult | null, chart: ReturnType<DashboardComponent['newMonthlyChart']>): void {
        const items = result?.items || [];
        const names: string[] = [];
        items.forEach((it) => (it.unities || []).forEach((u) => names.push(u.unityName)));
        const unityNames = Array.from(new Set(names));
        chart.columns = ['Mês', ...unityNames];
        chart.data = items.map((it) => {
            const byName = new Map<string, number>(
                (it.unities || []).map((u) => [u.unityName, u.total] as [string, number]),
            );
            return [it.monthLabel, ...unityNames.map((n) => (byName.has(n) ? (byName.get(n) as number) : 0))];
        });
    }

    /** true só quando há >= 2 colunas (Mês + 1 unidade) e ao menos uma linha — evita o erro de colunas insuficientes. */
    hasMonthlyData(chart: ReturnType<DashboardComponent['newMonthlyChart']>): boolean {
        return chart.columns.length > 1 && chart.data.length > 0;
    }

    private loadAwardedList(): void {
        this.dashboardApi.awardedIdeasList({ ...this.buildPerfInput(), page: this.awardedPage }).subscribe({
            next: ({ data }) => { this.awardedList = data.awardedIdeasListDashboard; },
            error: (err) => super.onError(err),
        });
    }

    nextAwardedPage(): void {
        if (this.awardedList?.nextPage) { this.awardedPage++; this.loadAwardedList(); }
    }

    prevAwardedPage(): void {
        if (this.awardedList?.previouPage) { this.awardedPage--; this.loadAwardedList(); }
    }
}
